import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Lock, RefreshCw, KeyRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const post = async (path, token, body) => {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
  return data;
};

export const AdminGovernance = () => {
  const [status, setStatus] = useState(null);
  const [tokenInput, setTokenInput] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const authToken = localStorage.getItem('startech_token');

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/governance/status`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setStatus(await res.json());
    } catch (e) { setMsg(`Status error: ${e.message}`); }
  }, [authToken]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const run = async (path, label) => {
    if (!tokenInput || tokenInput.length < 8) { setMsg('Enter your token (min 8 chars).'); return; }
    setBusy(true); setMsg('');
    try {
      const data = await post(`/api/admin/governance/${path}`, authToken, { token: tokenInput });
      setMsg(`${label}: done ${JSON.stringify(data)}`);
      setTokenInput('');
      await loadStatus();
    } catch (e) { setMsg(`${label} refused: ${e.message}`); }
    finally { setBusy(false); }
  };

  const Chip = ({ ok, on, off }) => (
    <span className={`text-xs px-2 py-0.5 rounded-full ${ok ? 'bg-emerald-900/50 text-emerald-300' : 'bg-slate-800 text-slate-400'}`}>
      {ok ? on : off}
    </span>
  );

  return (
    <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 backdrop-blur-sm mb-8"
         data-testid="admin-governance-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={18} className="text-cyan-400" />
        <h3 className="text-lg font-semibold text-cyan-300">Governance Authority (Workstream 6 · SO-013)</h3>
      </div>
      <p className="text-sm text-slate-400 mb-4">
        Set or rotate the operator token, and drive the Judge unlock and fingerprint re-baseline.
        Only a one-way hash is stored on the server; the assistant never sees your token.
      </p>

      <div className="flex flex-wrap gap-3 mb-4" data-testid="admin-governance-status">
        <Chip ok={status?.token_configured} on="Token armed" off="Bootstrap (not armed)" />
        <Chip ok={status?.lock_held} on="Judge lock HELD" off="No Judge lock" />
        <Chip ok={status?.fingerprint_drift} on="Fingerprint drift" off="Fingerprints clean" />
      </div>

      <div className="flex items-center gap-2 mb-3">
        <KeyRound size={16} className="text-slate-400" />
        <input
          type="password"
          value={tokenInput}
          onChange={(e) => setTokenInput(e.target.value)}
          placeholder="Operator token"
          className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
          data-testid="admin-governance-token-input"
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <button disabled={busy} onClick={() => run('token', 'Set or rotate token')}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-sm text-white disabled:opacity-50"
          data-testid="admin-governance-set-token-btn">
          <KeyRound size={15} /> Set / rotate token
        </button>
        <button disabled={busy} onClick={() => run('unlock', 'Clear Judge lock')}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm text-white disabled:opacity-50"
          data-testid="admin-governance-unlock-btn">
          <Lock size={15} /> Clear Judge lock
        </button>
        <button disabled={busy} onClick={() => run('rebaseline', 'Re-baseline fingerprints')}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm text-white disabled:opacity-50"
          data-testid="admin-governance-rebaseline-btn">
          <RefreshCw size={15} /> Re-baseline fingerprints
        </button>
      </div>

      {msg && (
        <div className="mt-4 text-xs text-slate-300 bg-slate-950/80 border border-slate-800 rounded-lg p-3 break-all"
             data-testid="admin-governance-message">
          {msg}
        </div>
      )}
    </div>
  );
};

export default AdminGovernance;
